import React, { useState, useEffect } from 'react';
import { 
  X, Receipt, Calendar, Clock, User, PoundSterling,
  CheckCircle2, Ban, Mail, MessageSquare, Send, 
  ChevronRight, FileText, CreditCard, AlertCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Invoice Summary Modal - Squeegee-style invoice details
 * Shows full invoice details with cancel option
 */
const InvoiceSummaryModal = ({ 
  invoice, 
  onClose, 
  onCancelInvoice,
  onSendInvoice,
  onViewPayment,
  zIndex = null
}) => {
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(invoice);
  const [cancelling, setCancelling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  // Fetch full invoice data if only ID provided
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!invoice?.id && !invoice?.invoice_number) return;
      if (invoice?.items) {
        setInvoiceData(invoice);
        return;
      }
      
      setLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/invoices/${invoice.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setInvoiceData(data);
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [invoice]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Paid</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">Cancelled</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">Pending</span>;
      case 'overdue':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">Overdue</span>;
      default:
        return <span className="px-2 py-1 bg-zinc-100 text-zinc-700 rounded text-xs font-medium">{status || 'Unknown'}</span>;
    }
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this invoice? This action cannot be undone.')) return;
    
    setCancelling(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/invoices/${invoiceData.id}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        if (onCancelInvoice) onCancelInvoice(invoiceData);
        onClose();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to cancel invoice');
      }
    } catch (err) {
      console.error('Error cancelling invoice:', err);
      alert('Failed to cancel invoice');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" style={zIndex ? { zIndex } : undefined}>
        <div className="bg-white rounded-xl p-8">
          <Loader2 className="animate-spin text-emerald-500" size={32} />
        </div>
      </div>
    );
  }

  if (!invoiceData) return null;

  const isPaid = invoiceData.status === 'paid';
  const isCancelled = invoiceData.status === 'cancelled';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" style={zIndex ? { zIndex } : undefined} data-testid="invoice-summary-modal">
      {/* Header */}
      <div 
        className={`${isPaid ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' : isCancelled ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-zinc-700 to-zinc-800'} text-white px-4 py-4`}
        style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 16px)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <button onClick={onClose} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
            <X size={24} />
          </button>
          <h1 className="flex-1 text-lg font-semibold mx-2">Invoice Summary</h1>
          {!isCancelled && !isPaid && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg flex items-center gap-1"
            >
              {cancelling ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
              Cancel Invoice
            </button>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xl font-bold">{invoiceData.invoice_number || 'INV-XXXX'}</p>
            <p className="text-sm opacity-80">{invoiceData.customer_name}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">£{(invoiceData.total || 0).toFixed(2)}</p>
            {getStatusBadge(invoiceData.status)}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Invoice Details Section */}
        <div className="p-4 border-b border-zinc-100">
          <h2 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
            <FileText size={18} />
            Invoice Details
          </h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-zinc-500">Status</span>
              <div>{getStatusBadge(invoiceData.status)}</div>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Invoice No</span>
              <span className="font-medium">{invoiceData.invoice_number}</span>
            </div>
            {invoiceData.reference && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Reference No</span>
                <span className="font-medium">{invoiceData.reference}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-zinc-500">Due Date</span>
              <span className="font-medium">{formatShortDate(invoiceData.due_date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Created</span>
              <span className="font-medium">{formatDate(invoiceData.created_at)}</span>
            </div>
            {invoiceData.paid_at && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Paid</span>
                <span className="font-medium text-emerald-600">{formatDate(invoiceData.paid_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Invoice Items Section */}
        <div className="p-4 border-b border-zinc-100">
          <h2 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
            <Receipt size={18} />
            Invoice Items
          </h2>
          
          <div className="space-y-3">
            {(invoiceData.items || []).map((item, idx) => (
              <div key={idx} className="flex justify-between items-start py-2 border-b border-zinc-50 last:border-0">
                <div>
                  <p className="font-medium text-zinc-900">{item.description}</p>
                  <p className="text-sm text-zinc-500">Qty: {item.quantity || 1}</p>
                </div>
                <p className="font-semibold text-zinc-900">£{(item.unit_price || item.amount || 0).toFixed(2)}</p>
              </div>
            ))}
            
            {/* Totals */}
            <div className="pt-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-zinc-500">Subtotal</span>
                <span className="font-medium">£{(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}</span>
              </div>
              {invoiceData.vat_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">VAT ({invoiceData.vat_rate || 20}%)</span>
                  <span className="font-medium">£{(invoiceData.vat_amount || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-emerald-600">£{(invoiceData.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payments Section */}
        {(invoiceData.payments || []).length > 0 && (
          <div className="p-4 border-b border-zinc-100">
            <h2 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
              <CreditCard size={18} />
              Payments
            </h2>
            
            <div className="space-y-3">
              {invoiceData.payments.map((payment, idx) => (
                <button
                  key={idx}
                  onClick={() => onViewPayment && onViewPayment(payment)}
                  className="w-full p-3 bg-zinc-50 rounded-lg flex items-center justify-between hover:bg-zinc-100"
                >
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">£{(payment.amount || 0).toFixed(2)}</span>
                      {getStatusBadge(payment.status)}
                    </div>
                    <p className="text-sm text-zinc-500">{payment.description || payment.method || 'Payment'}</p>
                    <p className="text-xs text-zinc-400">{formatShortDate(payment.created_at)}</p>
                  </div>
                  <ChevronRight size={20} className="text-zinc-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notifications Section */}
        <div className="p-4">
          <h2 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
            <Mail size={18} />
            Invoice Notifications
          </h2>
          
          <div className="space-y-2">
            {invoiceData.email_sent && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                <Mail size={16} className="text-blue-500" />
                <span className="text-sm text-blue-700">Email sent {formatShortDate(invoiceData.email_sent_at)}</span>
              </div>
            )}
            {invoiceData.sms_sent && (
              <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                <MessageSquare size={16} className="text-purple-500" />
                <span className="text-sm text-purple-700">SMS sent {formatShortDate(invoiceData.sms_sent_at)}</span>
              </div>
            )}
            {!invoiceData.email_sent && !invoiceData.sms_sent && (
              <div className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg">
                <AlertCircle size={16} className="text-zinc-400" />
                <span className="text-sm text-zinc-500">No notifications sent yet</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Buttons */}
      {!isCancelled && (
        <div className="p-4 border-t border-zinc-200 bg-white" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
          <div className="flex gap-3">
            <button
              onClick={async () => {
                if (!onSendInvoice) return;
                setSendingEmail(true);
                try {
                  await onSendInvoice(invoiceData, 'email');
                  setInvoiceData(prev => ({
                    ...prev,
                    email_sent: true,
                    email_sent_at: new Date().toISOString()
                  }));
                } finally {
                  setSendingEmail(false);
                }
              }}
              disabled={sendingEmail}
              className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="send-email-btn"
            >
              {sendingEmail ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </button>
            <button
              onClick={async () => {
                if (!onSendInvoice) return;
                setSendingSms(true);
                try {
                  await onSendInvoice(invoiceData, 'sms');
                  setInvoiceData(prev => ({
                    ...prev,
                    sms_sent: true,
                    sms_sent_at: new Date().toISOString()
                  }));
                } finally {
                  setSendingSms(false);
                }
              }}
              disabled={sendingSms}
              className="flex-1 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="send-sms-btn"
            >
              {sendingSms ? <Loader2 size={18} className="animate-spin" /> : <MessageSquare size={18} />}
              {sendingSms ? 'Sending...' : 'Send SMS'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceSummaryModal;
